import { useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { IS_NATIVE_APP } from "../config/appTarget.js";
import { useSwipeLimits } from "../hooks/useSwipeLimits.js";
import { useHideOnScroll } from "../hooks/useHideOnScroll.js";
import { openExternalUrl } from "../lib/openExternalUrl.js";
import {
  ANDROID_STORE_URL,
  IOS_STORE_URL,
  PLAY_STORE_AVAILABLE,
  detectMobilePlatform,
} from "../lib/appStoreLinks.js";
import { PAGE_GUTTER_CLASS } from "./PageContainer.js";

const DISMISS_KEY = "pawseum_app_floater_dismissed";
const HIDE_ON_PATHS = new Set([
  "/animais",
  "/caes",
  "/caes/adotados",
  "/gatos",
  "/gatos/adotados",
])

function wasDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

/** Apple logo (inherits `currentColor`). */
const AppleLogo = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 384 512"
    className={className}
    aria-hidden="true"
    fill="currentColor"
  >
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM262.1 104.5c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

/** Colourful Google Play logo. */
const GooglePlayLogo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 256 283" className={className} aria-hidden="true">
    <path
      d="M119.553 134.916 1.06 259.061a32.14 32.14 0 0 0 47.062 19.071l133.327-75.934-61.896-67.282z"
      fill="#EA4335"
    />
    <path
      d="m239.37 113.814-57.635-33.33-64.699 57.596 64.953 64.945 57.28-32.955a32.16 32.16 0 0 0 .1-56.256z"
      fill="#FBBC04"
    />
    <path
      d="M1.06 23.487A32.14 32.14 0 0 0 0 31.61v219.327c.002 2.755.356 5.499 1.06 8.124l122.61-121.94L1.06 23.486z"
      fill="#4285F4"
    />
    <path
      d="M120.436 141.372 181.744 80.49 48.576 4.229A32.155 32.155 0 0 0 1.06 23.487l119.376 117.885z"
      fill="#34A853"
    />
  </svg>
);

interface StoreOptionProps {
  title: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  comingSoon?: boolean;
}

const StoreOption = ({
  title,
  label,
  icon,
  onClick,
  comingSoon,
}: StoreOptionProps) => {
  const { t } = useTranslation();
  return (
  <div className="flex flex-1 flex-col items-center gap-2">
    <span className="text-xs font-bold uppercase tracking-wide text-my-black/60">
      {title}
    </span>
    <button
      type="button"
      onClick={comingSoon ? undefined : onClick}
      disabled={comingSoon}
      className={`flex w-full flex-col items-center gap-3 rounded-2xl px-4 py-6 text-white
        focus:outline-none focus:ring-2 focus:ring-gradient-blue ${
          comingSoon
            ? "bg-black/40 cursor-not-allowed"
            : "bg-black transition-transform hover:scale-[1.03]"
        }`}
    >
      <span
        className={`flex h-12 w-12 items-center justify-center ${comingSoon ? "opacity-60" : ""}`}
      >
        {icon}
      </span>
      <span className="text-base font-semibold">
        {comingSoon ? t("appDownload.comingSoon") : label}
      </span>
    </button>
  </div>
  );
};

/** Modal presenting both stores in full size. */
const AppDownloadModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  return (
  <div
    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    role="dialog"
    aria-modal="true"
    aria-label={t("appDownload.ariaLabel")}
    onClick={onClose}
  >
    <div
      className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t("actions.close")}
        className="absolute right-3 top-3 rounded-full p-1 text-my-black/50 hover:text-my-black
          focus:outline-none focus:ring-2 focus:ring-gradient-blue"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>

      <h2 className="text-center text-xl font-bold text-main-purple">
        {t("appDownload.modalTitle")}
      </h2>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row">
        <StoreOption
          title="iOS"
          label="App Store"
          icon={<AppleLogo className="h-11 w-11" />}
          onClick={() => openExternalUrl(IOS_STORE_URL)}
        />
        <StoreOption
          title="Android"
          label="Play Store"
          icon={<GooglePlayLogo className="h-11 w-11" />}
          comingSoon={!PLAY_STORE_AVAILABLE}
          onClick={() => openExternalUrl(ANDROID_STORE_URL)}
        />
      </div>
    </div>
  </div>
  );
};

/**
 * Bottom-left floating "Descarrega a app" call-to-action shown on every web
 * screen. It has no background of its own; clicking it opens a modal with both
 * stores in full size. Hidden inside the native shells and for users who already
 * have the app tier (they installed it). Dismissible per browser session — it
 * comes back on the next visit until the user installs.
 */
const AppDownloadFloater = () => {
  const { t } = useTranslation();
  const { tier } = useSwipeLimits();
  const [dismissed, setDismissed] = useState(() => wasDismissedThisSession());
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // Landing fades after ~1.4 screens; every other page hides on any scroll.
  const isLanding = pathname === "/" || pathname === "/adotar";
  const hidden = useHideOnScroll(isLanding ? 1.4 : 0);

  // Never on native; never once the user already has the app tier.
  if (IS_NATIVE_APP || tier === "app" || dismissed || HIDE_ON_PATHS.has(pathname)) return null;

  const dismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Ignore storage failures (private mode) — just hide for now.
    }
    setDismissed(true);
  };

  // On phones, jump straight to the matching store; on desktop (no mobile OS to
  // detect) open the modal that offers both stores.
  const handleOpen = () => {
    const platform = detectMobilePlatform();
    if (platform === "ios") {
      openExternalUrl(IOS_STORE_URL);
      return;
    }
    // Android isn't public yet (and desktop can't pick a phone OS) — open the
    // modal so the user sees iOS + the "Brevemente" Play Store state.
    setOpen(true);
  };

  return (
    <>
      <style>{`
        .specular-btn::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(100deg, transparent 35%, rgba(255,255,255,0.5) 50%, transparent 65%);
          transform: translateX(-130%) skewX(-18deg);
          animation: specularSweep 5s ease-in-out infinite;
          pointer-events: none;
        }
        .specular-btn::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: radial-gradient(90px circle at var(--mx, 50%) var(--my, 0%), rgba(255,255,255,0.4), transparent 60%);
          opacity: 0;
          transition: opacity .25s ease;
          pointer-events: none;
        }
        .specular-btn:hover::after { opacity: 1; }
        @keyframes specularSweep {
          0% { transform: translateX(-130%) skewX(-18deg); }
          60%, 100% { transform: translateX(160%) skewX(-18deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .specular-btn::before { animation: none; opacity: 0; }
        }
      `}</style>
      <div
        className={`fixed inset-x-0 z-40 pointer-events-none flex justify-start transition-opacity duration-500 ${hidden ? "opacity-0" : "opacity-100"} ${PAGE_GUTTER_CLASS}`}
        style={{
          bottom: IS_NATIVE_APP
            ? "calc(env(safe-area-inset-bottom) + 96px)"
            : "1.5rem",
        }}
      >
        <div
          className={`group relative ml-2 ${hidden ? "pointer-events-none" : "pointer-events-auto"}`}
        >
        <button
          type="button"
          onClick={handleOpen}
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
            e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
          }}
          aria-label={t("appDownload.ariaLabel")}
          className="specular-btn relative overflow-hidden flex items-center gap-2 rounded-full
            bg-gradient-to-b from-neutral-700 to-my-black px-3.5 py-2 text-sm font-bold text-white
            ring-1 ring-white/15 shadow-[0_10px_20px_-6px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.25)]
            transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gradient-blue
            md:px-5 md:py-2.5"
        >
          <span className="relative z-10 flex items-center gap-2">
            <Smartphone className="h-[18px] w-[18px]" aria-hidden="true" />
            <span>{t("appDownload.cta")}</span>
            <AppleLogo className="h-[18px] w-[18px]" />
            <GooglePlayLogo className="h-[18px] w-[18px]" />
          </span>
        </button>

        <button
          type="button"
          onClick={dismiss}
          aria-label={t("actions.dismiss")}
          className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full
            bg-my-black text-white shadow ring-1 ring-white/70 transition-opacity
            opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
        </div>
      </div>

      {open && <AppDownloadModal onClose={() => setOpen(false)} />}
    </>
  );
};

export default AppDownloadFloater;
